import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiPaperclip, BiTrash, BiUpload } from 'react-icons/bi';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { useUploadToCDNMutation } from '@/app/services/chatbot-rtk.services';
import { PSButton } from '@/components/ui/button';
import { PSInput } from '@/components/ui/input';
import { AlertsType } from '@/constants/alerts.constant';
import { selectUseId, selectUserProfile } from '@/features/auth/auth.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import AddNoteButton from '../../AddNoteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import VideoScenarioDeleteButton from './video-scenario-delete-button';
import ScenarioSide from './video-scenario-side';

interface IFormData extends INoteForm {
  title: string;
  label: string;
  media?: {
    path: string;
  };
}

export const MacBotAttachmentForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
  config,
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
  parentId: string | null;
  config: any;
  tree: any[];
}) => {
  const { t } = useTranslation();

  const { user_identification } = useAppSelector(selectUserProfile);
  const userId = useAppSelector(selectUseId);

  const [isDelete, setIsDelete] = useState(true);
  const [files, setFiles] = useState<any[]>([]);
  const [fileRejections, setFileRejections] = useState<FileRejection[]>([]);

  const [cdnUpload, cdnUploadResults] = useUploadToCDNMutation();

  const schema = Yup.object({
    title: Yup.string().required(t(AlertsType.ISKBKO025)).max(30, t('Wizard/validationLabels/key16')),
    label: Yup.string().required(t('MacBotAttachmentForm/key16') + ' ' + t('MacBotAttachmentForm/scenario_variable_name_required')),
    media: Yup.object().shape({
      path: Yup.string().optional().nullable(),
    }),
    ...getNoteSchema(t),
  });

  const {
    register,
    formState: { errors },
    reset,
    setValue,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    control,
    getValues,
  } = useForm<IFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
  });
  const { fields } = useFieldArray({
    control,
    name: 'notes',
  });

  const formatExtensions = ['.pdf', '.doc', '.docx', '.txt', '.rtf', '.xls', '.xlsx', '.csv', '.ppt', '.pptx', '.zip', '.rar', '.md', '.json', '.xml'];

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    maxSize: 30 * 1024 * 1024, //30mb max size
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'application/rtf': ['.rtf'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': [',csv'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'application/zip': ['.zip'],
      'application/x-rar-compressed': ['.rar'],
      'text/markdown': ['.md'],
      'application/json': ['.json'],
      'application/xml': ['.xml'],
    },
    onDrop: acceptedFiles => {
      setFiles(
        acceptedFiles.map(file =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      );

      clearErrors('media.path');
    },
    onDropRejected(fileRejections, event) {
      setFileRejections(fileRejections);
    },
  });

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    <li key={file.name}>
      {file.name} -{' '}
      {Intl.NumberFormat('en', {
        notation: 'compact',
        style: 'unit',
        unit: 'byte',
        unitDisplay: 'narrow',
      }).format(file.size)}
      <ul>
        {errors.map(e => (
          <li key={e.code} className="tw-text-red-600 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mt-3">
            {e.code === 'file-too-large' ? t('MacBotAttachmentForm/key8') : e.message}
          </li>
        ))}
      </ul>
    </li>
  ));

  const handleDeleteSelectedFile = () => {
    setFiles([]);
    setFileRejections([]);

    setValue('media.path', '');
  };

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
    setShow(false);
  };

  const onSubmit = (data: any) => {
    if (!files || files.length <= 0)
      return setError('media.path', {
        type: 'required',
        message: t('MacBotAttachmentForm/key12'),
      });

    const formData = new FormData();
    formData.append('files', files[0], user_identification + '_' + files[0].name);

    cdnUpload(formData)
      .unwrap()
      .then(result => {
        if (result[0]?.is_successful) {
          const path = result[0].cdnPath;
          const name = result[0].original_name;
          const size = result[0].size_byte;

          if (itemData) {
            const _data = {
              ...itemData,
              id: itemData.id,
              title: data.title,
              label: data.label,
              type: BlockType.Attachment,
              parent: itemData.parent,
              ...data,
              media: {
                path,
                name,
                size,
              },
            };
            changeScenarioBlock(_data).then(() => {
              reset();
              setShow(false);
            });
          } else {
            const _data = {
              type: BlockType.Attachment,
              title: data.title,
              label: data.label,
              parent: parentId,
              ...data,
              media: {
                path,
                name,
                size,
              },
            };
            createScenarioBlock(_data).then(() => {
              reset();
              setShow(false);
            });
          }
        } else
          setError('media.path', {
            type: 'custom',
            message: t('MacBotAttachmentForm/key13'),
          });
      })
      .catch(err => {
        setError('media.path', {
          type: 'custom',
          message: err.data.message || err.message || t('MacBotAttachmentForm/key13'),
        });
      });
  };

  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    } else {
      reset({
        title: itemData?.title ?? '',
        label: itemData?.label ?? '',
        media: {
          path: itemData?.media.path ?? '',
        },
        notes: itemData?.notes,
      });
    }
  }, [itemData]);

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => files.forEach(file => URL.revokeObjectURL(file.preview));
  }, []);

  useEffect(() => {
    const createFileFromEditData = async (media: { path: string; name: string; size: number }) => {
      try {
        const response = await fetch(media.path);
        const blob = await response.blob();

        const file = new File([blob], media.name, {
          type: response.headers.get('content-type') as string,
        });

        Object.assign(file, {
          preview: media.path,
        });

        return setFiles([file]);
      } catch (error) {
        console.error('Error creating file:', error);
        return null;
      }
    };

    if (itemData?.media && Object.keys(itemData.media).length > 0) createFileFromEditData(itemData.media);
  }, [itemData?.media]);

  return (
    <React.Fragment>
      <ScenarioSide onSubmit={handleSubmit(onSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowAttachmentForm}
          onClose={() => {
            reset();
          }}
        />

        <div className="md:tw-p-6 tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <Row>
            <Col>
              <div className="tw-self-stretch tw-h-[68px] tw-flex-col tw-justify-center tw-items-start tw-gap-2 tw-flex">
                <div className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{t('MacBotAttachmentForm/key2')}</div>
                <Form.Group className="tw-w-full">
                  <Form.Control
                    max={31}
                    isInvalid={!!errors.title}
                    maxLength={31}
                    {...register('title')}
                    className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                  />
                  <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                    {errors.title?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <div />
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <Form.Group>
                <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {t('MacBotAttachmentForm/key16')}
                </Form.Label>
                <Form.Control
                  className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                  isInvalid={!!errors.label}
                  {...register(`label`)}
                />
                <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                  {errors.label?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>

          <section className="file-manager tw-w-full">
            {files.length <= 0 && (
              <React.Fragment>
                <div className="tw-self-stretch tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mb-4">
                  {t('MacBotAttachmentForm/key4')}
                </div>

                <div {...getRootProps({ className: 'dropzone' })}>
                  <PSInput {...getInputProps()} />
                  <div className="tw-min-h-[156px] tw-w-full tw-px-4 tw-py-9 tw-bg-gray-100 tw-rounded-md tw-border-dashed tw-border-2 tw-border-gray-300 tw-flex-col tw-justify-center tw-items-center tw-gap-2 tw-inline-flex">
                    <div className="tw-p-2 tw-bg-gray-200 tw-rounded-[99px] tw-justify-start tw-items-center tw-gap-2.5 tw-inline-flex">
                      <BiUpload className="tw-text-primary" size={22} />
                    </div>
                    <div className="tw-flex-col tw-justify-center tw-items-center tw-flex">
                      <div className="tw-min-w-[188px] tw-text-center tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                        <span className="tw-cursor-pointer tw-text-blue-700">
                          {t('MacBotAttachmentForm/key14')} {t('MacBotAttachmentForm/key15')}
                        </span>{' '}
                        {t('MacBotAttachmentForm/key5')}
                      </div>
                      <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none tw-text-center">
                        {t('MacBotAttachmentForm/key6')}{' '}
                        {formatExtensions.map((ext, index) => (
                          <span key={ext}>
                            {ext.replace('.', '').toUpperCase()}
                            {index !== formatExtensions.length - 1 && ', '}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            )}

            {!!errors.media?.path && (
              <div className="tw-text-red-600 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mt-3">{errors.media?.path?.message}</div>
            )}

            <ul className="tw-mt-4">{fileRejectionItems}</ul>

            <aside className="tw-mt-3">
              {files.map(file => (
                <div
                  key={file.name}
                  className="tw-w-full tw-h-[88px] tw-p-6 tw-bg-white tw-rounded-md tw-border tw-border-solid tw-border-gray-200 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-inline-flex"
                >
                  <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                    <div className="tw-justify-start tw-items-center tw-gap-3 tw-flex">
                      <div className="tw-w-10 tw-h-10 tw-bg-gray-200 tw-rounded tw-flex tw-flex-col tw-items-center tw-justify-center tw-shrink-0">
                        <BiPaperclip size={22} className="tw-text-gray-400 tw-text-xl" />
                      </div>
                      <div className="tw-flex-col tw-justify-center tw-items-start tw-gap-1 tw-inline-flex">
                        <div className="tw-text-left tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-max-w-40 tw-text-wrap tw-break-words">
                          {file.name}
                        </div>
                        {watch('label') && <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{file.label}</div>}
                      </div>
                    </div>
                    <div className="tw-justify-end tw-items-center tw-gap-6 tw-flex">
                      <div
                        onClick={() => window.open(file.preview, '_blank')}
                        onKeyDown={() => window.open(file.preview, '_blank')}
                        role="button"
                        id="btn-video-attachment-open"
                        tabIndex={0}
                        className="tw-text-center tw-text-blue-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                      >
                        {t('MacBotAttachmentForm/key7')}
                      </div>
                      <PSButton
                        id="btn-delete-selected-file"
                        className="tw-bg-transparent tw-border-none"
                        type="button"
                        onClick={handleDeleteSelectedFile}
                        icon={<BiTrash size={22} className="tw-text-gray-400" />}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </aside>
          </section>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {<VideoScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />}

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton
              id="btn-attachment-submit"
              type="submit"
              disabled={loading || cdnUploadResults.isLoading}
              isLoading={loading || cdnUploadResults.isLoading}
              title={t('MacBotResponseForm/btn-02')}
            />
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
      </ScenarioSide>
    </React.Fragment>
  );
};
